// =================================================================
// Engine: Monitoring module (PSD §4.2 / TODO Task 1.7, 2.8)
// =================================================================
import { LevelType, Strategy, } from '@amino-pk/shared-types';
import { HARTFORD_WINDOW_HR } from './constants';
const MS_PER_MIN = 60_000;
/**
 * Sampling times for the active strategy (PSD §4.2).
 * EI → Hartford random (6–14h). Traditional → peak (post-infusion) + trough (pre-next-dose).
 * Wall-clock times computed when infusionStartISO is known.
 */
export function computeSamplingTimes(strategy, infusionDurationMin = 30) {
    const startISO = strategy.infusionStartISO;
    const infusionEndMs = startISO ? new Date(startISO).getTime() + infusionDurationMin * MS_PER_MIN : null;
    function wallClock(offsetFromInfusionEndMin) {
        if (infusionEndMs == null)
            return undefined;
        return new Date(infusionEndMs + offsetFromInfusionEndMin * MS_PER_MIN).toISOString();
    }
    if (strategy.strategy === Strategy.ExtendedInterval) {
        const [lo, hi] = HARTFORD_WINDOW_HR;
        const midMin = ((lo + hi) / 2) * 60;
        return [
            {
                label: 'Hartford random',
                type: LevelType.Random,
                offsetFromInfusionEndMin: midMin,
                targetWindowMin: [lo * 60, hi * 60],
                wallClockISO: wallClock(midMin),
            },
        ];
    }
    if (strategy.strategy === Strategy.Traditional) {
        const peakMin = 30; // 30 min post-infusion end
        const troughMin = 24 * 60 - infusionDurationMin; // just before next dose (~24h)
        return [
            {
                label: 'Peak',
                type: LevelType.Peak,
                offsetFromInfusionEndMin: peakMin,
                targetWindowMin: [peakMin - 10, peakMin + 10],
                wallClockISO: wallClock(peakMin),
            },
            {
                label: 'Trough',
                type: LevelType.Trough,
                offsetFromInfusionEndMin: troughMin,
                targetWindowMin: [troughMin - 30, troughMin],
                wallClockISO: wallClock(troughMin),
            },
        ];
    }
    // Dialysis / synergy — predialysis or trough-based
    return [
        {
            label: 'Predialysis / Trough',
            type: LevelType.Trough,
            offsetFromInfusionEndMin: 24 * 60,
            targetWindowMin: [23 * 60, 24 * 60],
            wallClockISO: wallClock(24 * 60),
        },
    ];
}
/** SCr/BUN monitoring frequency by renal stability + toxicity (PSD §4.2).
 *  `nephrotoxinFrequencyAdjDays` (FE-E4) optionally tightens the cadence:
 *  take min(existing, adjDays) and append a nephrotoxin note when the
 *  adjustment wins.
 */
export function computeScrBunFrequency(renal, safety, nephrotoxinFrequencyAdjDays) {
    let days;
    let reason;
    if (safety?.toxicityRisk === 'high') {
        days = 1;
        reason = 'High toxicity risk — daily SCr/BUN monitoring.';
    }
    else if (renal.stability === 'unstable') {
        days = 1;
        reason = 'Unstable renal function — SCr/BUN every 1–2 days.';
    }
    else {
        days = 3;
        reason = 'Stable renal function — SCr/BUN every 2–3 days.';
    }
    if (nephrotoxinFrequencyAdjDays != null && nephrotoxinFrequencyAdjDays > 0) {
        if (nephrotoxinFrequencyAdjDays < days) {
            days = nephrotoxinFrequencyAdjDays;
            reason = `${reason} Tightened to every ${days} day(s) due to concurrent nephrotoxins.`;
        }
    }
    return { days, reason };
}
/** Monitoring pathway label by strategy. */
export function monitoringPathway(strategy) {
    switch (strategy.strategy) {
        case Strategy.ExtendedInterval:
            return 'nomogram';
        case Strategy.Traditional:
            return 'two-level';
        case Strategy.Dialysis:
            return 'dialysis';
        case Strategy.Synergy:
            return 'trough-only';
        default:
            return 'nomogram';
    }
}
/** Full monitoring schedule (PSD §4.2). `nephrotoxinFrequencyAdjDays` (FE-E4)
 *  optionally tightens the SCr/BUN cadence. */
export function computeMonitoringSchedule(strategy, renal, safety, infusionDurationMin = 30, nephrotoxinFrequencyAdjDays) {
    const samplingSchedule = computeSamplingTimes(strategy, infusionDurationMin);
    const freq = computeScrBunFrequency(renal, safety, nephrotoxinFrequencyAdjDays);
    const reassessmentTriggers = [
        'Renal function decline (SCr ↑ ≥ 0.3 mg/dL or ↑ ≥ 1.5× baseline)',
        'Interval change',
        'Missed sample / late draw',
    ];
    return {
        samplingSchedule,
        monitoringPathway: monitoringPathway(strategy),
        scrBunFrequencyDays: freq.days,
        scrBunFrequencyReason: freq.reason,
        reassessmentTriggers,
        troughMinimizationTarget: strategy.strategy === Strategy.ExtendedInterval ? 0.5 : undefined,
    };
}
//# sourceMappingURL=monitoring.js.map